import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useContext,
} from "react";
import {
  useLoadScript,
  GoogleMap,
  Marker,
  StandaloneSearchBox,
  MarkerClusterer,
  DirectionsRenderer,
  InfoWindow,
} from "@react-google-maps/api";
import Geocode from "react-geocode";

import MyLocationIcon from "@mui/icons-material/MyLocation";
import DirectionsIcon from "@mui/icons-material/Directions";

import { CurrentLocationStyled, DirectionsStyled } from "../../theme";
import { getTranslation, LanguageContext } from "../languageProvider";
import { Log } from "./utility";
import { AuthContext } from "../authContextProvider";

Geocode.setApiKey(process.env.REACT_APP_GOOGLE_MAPS_API);
Geocode.enableDebug();

let currentMap = null;

export const Maps = React.memo(
  ({
    list,
    draggable,
    DragEnd,
    mapHeight,
    Select,
    MyLocationHandler,
    mapReload,
  }) => {
    const searchPlaceRef = useRef();

    const { lang } = useContext(LanguageContext);
    const { snackbarHandler } = useContext(AuthContext);

    const [isMap, setIsMap] = useState(false);
    const [directions, setDirections] = useState(null);
    const center = { lat: 39.768372, lng: -8.185234 };
    const { isLoaded } = useLoadScript({
      googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API,
      libraries: ["places"],
      language: lang,
    });

    let markerPosition = list.map((item) => ({
      location: item.pos,
      stopover: true,
    }));

    const loadHandler = (map) => {
      currentMap = map;
      setIsMap(true);
    };

    useEffect(() => {
      if (isMap) {
        centerAndZoom(list, null);
      }
    }, [list, isMap, mapReload]);

    const onPlacesChanged = (value) => {
      Geocode.fromAddress(value ? value : searchPlaceRef.current.value).then(
        (response) => {
          let place = response.results[0];
          let bounds = new window.google.maps.LatLngBounds();
          bounds.extend(place.geometry.viewport.northeast);
          bounds.extend(place.geometry.viewport.southwest);
          currentMap.fitBounds(bounds);
        },
        (error) => {
          Log("onPlacesChanged -> error", error);
        }
      );
    };
    const doubleClickHandler = useCallback(
      (event) => {
        if (event.latLng && draggable) {
          let toSend = {
            lat: Number(event.latLng.lat()).toFixed(8),
            lng: Number(event.latLng.lng()).toFixed(8),
          };

          let geocoder = new window.google.maps.Geocoder();
          geocoder.geocode(
            {
              location: { lat: event.latLng.lat(), lng: event.latLng.lng() },
            },
            function (results, status) {
              let address = "";
              if (status === "OK") {
                if (results[0]) {
                  address = results[0].formatted_address;
                } else {
                  Log("Error on get address");
                }
              } else {
                Log("Error on get address");
              }
              toSend.address = address;
              DragEnd(toSend);
            }
          );
        }
      },
      [DragEnd, draggable]
    );
    const showMyLocationClickHandler = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            centerAndZoom(list, {
              lat: Number(position.coords.latitude.toFixed(8)),
              lng: Number(position.coords.longitude.toFixed(8)),
            });
          },
          () => {
            snackbarHandler(getTranslation(`error500`));
          }
        );
      } else {
        // Browser doesn't support Geolocation
        snackbarHandler(getTranslation(`errorGeolocation`));
      }
    };

    const centerAndZoom = (listOfMarkers, myLocation) => {
      let bounds = new window.google.maps.LatLngBounds();

      if (listOfMarkers.length === 0) {
        onPlacesChanged("Portugal");
      }

      for (let marker of listOfMarkers) {
        bounds.extend(marker.pos);
      }
      if (myLocation) {
        MyLocationHandler(myLocation);
        bounds.extend(myLocation);
      }
      currentMap.fitBounds(bounds);
    };

    const showDirectionOnMap = (myLocation) => {
      const directionsService = new window.google.maps.DirectionsService();
      let lastPointOnList = list.length > 0 ? list[list.length - 1] : null;
      let destinationLocation = lastPointOnList
        ? lastPointOnList.pos
        : myLocation;
      directionsService.route(
        {
          origin: myLocation,
          destination: destinationLocation,
          travelMode: "DRIVING",
          waypoints: markerPosition,
        },
        function (response, status) {
          let wayPoints = "";
          let newList = list.filter((item) => item.id !== lastPointOnList.id);
          if (newList.length > 0) {
            for (let point of newList) {
              wayPoints += `|${point.pos.lat},${point.pos.lng}`;
            }
          }
          if (status === "OK") {
            let URL = `https://www.google.com/maps/dir/?api=1&origin=${
              myLocation.lat
            },${myLocation.lng}&destination=${destinationLocation.lat},${
              destinationLocation.lng
            }&travelmode=driving${wayPoints && `&waypoints=${wayPoints}`}`;
            setDirections(response);
            window.open(URL);
            //directionsRenderer.setDirections(response);
          } else {
            Log("Directions request failed due to " + status);
          }
        }
      );
    };

    const getDirectionsClickHandler = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            showDirectionOnMap({
              lat: Number(position.coords.latitude.toFixed(8)),
              lng: Number(position.coords.longitude.toFixed(8)),
            });
          },
          () => {
            snackbarHandler(getTranslation(`error500`));
          }
        );
      } else {
        // Browser doesn't support Geolocation
        snackbarHandler(getTranslation(`errorGeolocation`));
      }
    };

    const renderMap = () => (
      <GoogleMap
        defaultCenter={center}
        onDblClick={doubleClickHandler}
        onLoad={loadHandler}
        mapContainerStyle={{
          height: `${mapHeight}`,
          width: "100%",
        }}
        options={{
          panControl: true,
          mapTypeControl: true,
          scrollwheel: true,
          rotateControl: true,
          fullscreenControl: true,
          streetViewControl: true,
          zoomControl: true,
          scaleControl: true,
          styles: [
            {
              elementType: "geometry",
              stylers: [
                {
                  color: "#f5f5f5",
                },
              ],
            },
            {
              elementType: "labels.icon",
              stylers: [
                {
                  visibility: "off",
                },
              ],
            },
            {
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#616161",
                },
              ],
            },
            {
              elementType: "labels.text.stroke",
              stylers: [
                {
                  color: "#f5f5f5",
                },
              ],
            },
            {
              featureType: "administrative",
              elementType: "geometry",
              stylers: [
                {
                  visibility: "off",
                },
              ],
            },
            {
              featureType: "administrative.country",
              elementType: "geometry.stroke",
              stylers: [
                {
                  color: "#62aaff",
                },
                {
                  visibility: "on",
                },
                {
                  weight: 1,
                },
              ],
            },
            {
              featureType: "administrative.land_parcel",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#bdbdbd",
                },
              ],
            },
            {
              featureType: "poi",
              stylers: [
                {
                  visibility: "off",
                },
              ],
            },
            {
              featureType: "poi",
              elementType: "geometry",
              stylers: [
                {
                  color: "#eeeeee",
                },
              ],
            },
            {
              featureType: "poi",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#757575",
                },
              ],
            },
            {
              featureType: "poi.park",
              elementType: "geometry",
              stylers: [
                {
                  color: "#e5e5e5",
                },
              ],
            },
            {
              featureType: "poi.park",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#9e9e9e",
                },
              ],
            },
            {
              featureType: "road",
              stylers: [
                {
                  color: "#000",
                },
              ],
            },
            {
              featureType: "road",
              elementType: "geometry",
              stylers: [
                {
                  color: "#ffffff",
                },
              ],
            },
            {
              featureType: "road",
              elementType: "labels.icon",
              stylers: [
                {
                  visibility: "off",
                },
              ],
            },
            {
              featureType: "road.arterial",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#757575",
                },
              ],
            },
            {
              featureType: "road.highway",
              elementType: "geometry",
              stylers: [
                {
                  color: "#dadada",
                },
              ],
            },
            {
              featureType: "road.highway",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#616161",
                },
              ],
            },
            {
              featureType: "road.local",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#9e9e9e",
                },
              ],
            },
            {
              featureType: "transit",
              stylers: [
                {
                  visibility: "off",
                },
              ],
            },
            {
              featureType: "transit.line",
              elementType: "geometry",
              stylers: [
                {
                  color: "#e5e5e5",
                },
              ],
            },
            {
              featureType: "transit.station",
              elementType: "geometry",
              stylers: [
                {
                  color: "#eeeeee",
                },
              ],
            },
            {
              featureType: "water",
              elementType: "geometry",
              stylers: [
                {
                  color: "#c9c9c9",
                },
              ],
            },
            {
              featureType: "water",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#9e9e9e",
                },
              ],
            },
          ],
        }}
      >
        {directions && (
          <DirectionsRenderer directions={directions}></DirectionsRenderer>
        )}
        {currentMap && (
          <DirectionsStyled>
            <DirectionsIcon onClick={getDirectionsClickHandler} />
          </DirectionsStyled>
        )}
        {currentMap && (
          <CurrentLocationStyled>
            <MyLocationIcon onClick={showMyLocationClickHandler} />
          </CurrentLocationStyled>
        )}
        <StandaloneSearchBox
          onPlacesChanged={onPlacesChanged}
          data-translate="0107"
        >
          <input
            ref={searchPlaceRef}
            type="text"
            placeholder={getTranslation("0107")}
            style={{
              boxSizing: `border-box`,
              border: `1px solid transparent`,
              width: `260px`,
              height: `40px`,
              padding: `0 12px`,
              borderRadius: `4px`,
              boxShadow: `0 2px 2px rgba(0, 0, 0, 0.1)`,
              fontSize: `14px`,
              outline: `none`,
              textOverflow: `ellipses`,
              position: "absolute",
              top: "10px",
              left: "190px",
            }}
          />
        </StandaloneSearchBox>
        <MarkerClusterer
          options={{
            imagePath: "images/markerclusterer/m",
          }}
          minimumClusterSize={5}
        >
          {(clusterer) =>
            list.map((item, index) => {
              const dragEndHandler = (event) => {
                if (event.latLng && draggable) {
                  let toSend = {
                    lat: Number(event.latLng.lat()).toFixed(8),
                    lng: Number(event.latLng.lng()).toFixed(8),
                  };

                  let geocoder = new window.google.maps.Geocoder();
                  geocoder.geocode(
                    {
                      location: {
                        lat: event.latLng.lat(),
                        lng: event.latLng.lng(),
                      },
                    },
                    function (results, status) {
                      let address = "";
                      if (status === "OK") {
                        if (results[0]) {
                          address = results[0].formatted_address;
                        } else {
                          Log("Error on get address");
                        }
                      } else {
                        Log("Error on get address");
                      }
                      toSend.address = address;
                      DragEnd(toSend);
                    }
                  );
                }
              };
              const selectMarkerHandler = () =>
                Select && item.id !== 10 && Select(item);
              if (item.icon) {
                return (
                  <Marker
                    key={index}
                    position={item.pos}
                    clusterer={item.id !== 10 && clusterer}
                    onClick={item.id !== 10 && selectMarkerHandler}
                    draggable={item.id !== 10 && draggable}
                    onDragEnd={item.id !== 10 && dragEndHandler}
                    title={`${item.state && item.state.code}`}
                    icon={{
                      url: item.icon,
                      scaledSize: { width: 20, height: 20 },
                    }}
                    label={{
                      text: item.serialNumber,
                      className: "nameLabel",
                    }}
                  >
                    {/* <InfoWindow position={item.pos}>
                      <span> {item.serialNumber} </span>
                    </InfoWindow> */}
                  </Marker>
                );
              } else {
                return (
                  <Marker
                    key={index}
                    position={item.pos}
                    clusterer={item.id !== 10 && clusterer}
                    onClick={item.id !== 10 && selectMarkerHandler}
                    draggable={item.id !== 10 && draggable}
                    onDragEnd={item.id !== 10 && dragEndHandler}
                    icon={{
                      url:
                        item.id !== 10
                          ? "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png"
                          : "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                    }}
                  ></Marker>
                );
              }
            })
          }
        </MarkerClusterer>
      </GoogleMap>
    );
    return isLoaded ? renderMap() : null;
  }
);
